import './SupportBar.scss';
import { CSSProperties, Fragment, ReactNode, useSyncExternalStore } from 'react';
import clsx from 'clsx';
import { MdMic, MdSupportAgent, MdVolumeOff } from 'react-icons/md';
import type { IVoiceVolume } from '@srm/screen-sharing/light';
import { getInitials } from '@srm/ui/avatar';
import SupportPersonVolume from './SupportPersonVolume';
import { withPersonCard } from './withPersonCard';

export type SupportBarTone = 'neutral' | 'view' | 'control' | 'grey' | 'error';

/** Сотрудник в плашке: кружок с инициалами его цветом; `active` — управляет кабинетом */
export interface ISupportBarPerson {
    name: string;
    color: string;
    active?: boolean;
    /** Микрофон включён (IMPLEMENTATION.md §31) — значок у кружка */
    micOn?: boolean;
    /** Говорит сейчас — пульсирующее кольцо его цвета */
    speaking?: boolean;
    /** Ключ участника в `ISupportBarProps.speaking` (`peerId`): говорит ли он — оттуда, мимо пропсов полосы */
    speakingKey?: string;
    /** Пользователь-сотрудник: по наведению — его карточка вместо подсказки с именем */
    userKey?: string;
    /** Громкость сотрудника у пользователя (IMPLEMENTATION.md §42): по нажатию на кружок — меню громкости */
    volume?: IVoiceVolume;
    onVolumeChange?: (change: Partial<IVoiceVolume>) => void;
}

/** Кто говорит — несколько раз в секунду, мимо состояния полосы (IMPLEMENTATION.md §31) */
export interface ISupportBarSpeaking {
    get: () => readonly string[];
    subscribe: (listener: () => void) => () => void;
}

const NOBODY: readonly string[] = [];
const quiet: ISupportBarSpeaking = { get: () => NOBODY, subscribe: () => () => undefined };

const personTitle = (person: ISupportBarPerson) =>
    [person.name, person.active && 'управляет', person.speaking ? 'говорит' : person.micOn && 'микрофон включён']
        .filter(Boolean)
        .join(' — ');

export interface ISupportBarProps {
    /** `neutral` — ждём, `view` — смотрят, `control` — управление и вход от имени, `grey` — связь, `error` — отказ */
    tone: SupportBarTone;
    /** Имя сотрудника — инициалы в кружке перед текстом */
    person?: string | null;
    /** Пользователь-сотрудник `person`: по наведению на кружок — его карточка */
    personKey?: string;
    /** Несколько сотрудников — кружки их цветов вместо знака; имена — в тексте плашки */
    people?: ISupportBarPerson[];
    /** Кто говорит (`speakingKey` кружков): читают только кружки, полоса на каждую смену не перерисовывается */
    speaking?: ISupportBarSpeaking;
    /** Индикатор ожидания вместо инициалов */
    busy?: boolean;
    /** Свой знак перед текстом (иконка) — когда нет ни сотрудника, ни ожидания */
    icon?: ReactNode;
    /** Главная часть текста (жирная) */
    title: ReactNode;
    /** Продолжение после «·» */
    description?: ReactNode;
    /** Подсказка к тексту — полный текст, когда узкая плашка его сокращает */
    hint?: string;
    /** Состояние рядом с кнопками (качество связи) */
    aside?: ReactNode;
    /** Кнопки справа */
    actions?: ReactNode;
    /** Содержимое под текстом внутри карточки (поле комментария оценки) */
    details?: ReactNode;
    /** Панель, раскрытая из полосы (лента действий): позиционируется под полосой */
    children?: ReactNode;
    /** Имя области для чтения с экрана */
    label: string;
    /** Полоса без места в оболочке: прибита к верху окна */
    floating?: boolean;
    /** Текст переносится до трёх строк, продолжение — со второй (запрос с причиной); без него — строка с «…» */
    wrap?: boolean;
    /**
     * Как текст объявляется диктору: `polite` — свой `role="status"` (изменения текста), `alert` — сразу при появлении
     * (запрос согласия), `false` — объявляет владелец через постоянную live-область
     * @default 'polite'
     */
    live?: 'polite' | 'alert' | false;
    'data-testid'?: string;
}

/**
 * Системная плашка сеанса поддержки в зоне `.notes` оболочки — тем же языком, что «Что нового» и «Браузер устарел»
 * (карточка с корешком и плиткой-знаком): показ экрана у пользователя и вход «от имени» у сотрудника.
 * Помечена `data-remote-control-exclude`: команды удалённого управления её не нажимают, а блокировка ввода
 * пользователя её пропускает.
 */
const SupportBar = ({
    tone,
    person,
    personKey,
    people,
    speaking,
    busy,
    icon,
    title,
    description,
    hint,
    aside,
    actions,
    details,
    children,
    label,
    floating,
    wrap,
    live = 'polite',
    'data-testid': testId = 'support-bar',
}: ISupportBarProps) => (
    <section
        className={clsx(
            'support-bar',
            `support-bar--${tone}`,
            floating && 'support-bar--floating',
            wrap && 'support-bar--wrap',
        )}
        aria-label={label}
        data-remote-control-exclude
        data-testid={testId}
        data-tone={tone}
    >
        <div className="support-bar__card">
            {people?.length && !busy ? (
                <SupportBarPeople people={people} speaking={speaking ?? quiet} />
            ) : (
                withPersonCard(
                    !busy && person ? personKey : undefined,
                    person ?? '',
                    <span className="support-bar__badge" aria-hidden>
                        {busy ? (
                            <span className="support-bar__spinner" />
                        ) : person ? (
                            getInitials(person)
                        ) : (
                            (icon ?? <MdSupportAgent size={20} />)
                        )}
                    </span>,
                )
            )}
            {details ? (
                <div className="support-bar__main">
                    <SupportBarText title={title} description={description} hint={hint} wrap={wrap} live={live} />
                    {details}
                </div>
            ) : (
                <SupportBarText title={title} description={description} hint={hint} wrap={wrap} live={live} />
            )}
            {aside && <span className="support-bar__aside">{aside}</span>}
            {actions && <span className="support-bar__actions">{actions}</span>}
        </div>
        {children}
    </section>
);

/** Кружки сотрудников; «говорит» — из стора `speaking`, перерисовываются только они. */
const SupportBarPeople = ({ people, speaking }: { people: ISupportBarPerson[]; speaking: ISupportBarSpeaking }) => {
    const talking = useSyncExternalStore(speaking.subscribe, speaking.get);
    return (
        <span
            className="support-bar__people"
            // Кружки с меню громкости — кнопки: группа читается, без них — украшение рядом с текстом.
            {...(people.some((item) => item.onVolumeChange)
                ? { role: 'group', 'aria-label': 'Сотрудники поддержки' }
                : { 'aria-hidden': true })}
            data-testid="support-bar-people"
            title={people.some((item) => item.userKey) ? undefined : people.map((item) => item.name).join(', ')}
        >
            {people.map((person, index) => {
                const item =
                    person.speakingKey && talking.includes(person.speakingKey) ? { ...person, speaking: true } : person;
                const circle = (
                    <span
                        className="support-bar__person"
                        data-active={item.active || undefined}
                        data-speaking={item.speaking || undefined}
                        title={item.userKey ? undefined : personTitle(item)}
                        style={{ '--support-bar-person': item.color } as CSSProperties}
                    >
                        {getInitials(item.name)}
                        {item.volume?.muted ? (
                            <span className="support-bar__person-muted" data-testid="support-bar-person-muted">
                                <MdVolumeOff size={9} />
                            </span>
                        ) : (
                            item.micOn && (
                                <span className="support-bar__person-mic" data-testid="support-bar-person-mic">
                                    <MdMic size={9} />
                                </span>
                            )
                        )}
                    </span>
                );
                return (
                    <Fragment key={`${item.name}-${index}`}>
                        {item.volume && item.onVolumeChange ? (
                            <SupportPersonVolume
                                name={item.name}
                                userKey={item.userKey}
                                volume={item.volume}
                                onVolumeChange={item.onVolumeChange}
                            >
                                {circle}
                            </SupportPersonVolume>
                        ) : (
                            withPersonCard(item.userKey, item.name, circle)
                        )}
                    </Fragment>
                );
            })}
        </span>
    );
};

const SupportBarText = ({
    title,
    description,
    hint,
    wrap,
    live,
}: Pick<ISupportBarProps, 'title' | 'description' | 'hint' | 'wrap' | 'live'>) => (
    <span
        className="support-bar__text"
        title={hint}
        {...(live === 'alert' ? { role: 'alert' } : live === 'polite' ? { role: 'status', 'aria-live': 'polite' } : {})}
    >
        <b>{title}</b>
        {description &&
            (wrap ? (
                <span className="support-bar__description">{description}</span>
            ) : (
                <span className="support-bar__rest"> · {description}</span>
            ))}
    </span>
);

export default SupportBar;
